import { ref, type Ref } from 'vue';

import { api } from '@/api/client';
import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/**
 * 工程变更抽屉的共享零件：生成执行清单、催办措施、以及「五步接管了哪些格」。
 *
 * 这段逻辑 2026-08-18 曾在 ProjectSectionView 和 ModuleWorkbenchView 里
 * **各写了一遍**——补齐两处功能的代价是把重复扩大了一倍。
 * 抽成 composable 是「两处收敛」的第 0 步：先把已经重复的合掉，
 * 再谈把整个抽屉抽成共享组件。
 *
 * 只管变更这一块，不认识别的模块——共享抽屉是下一步的事，
 * 这里刻意不做成万能的，免得抽象跑在需求前面。
 */

/*
  原来这里还有一张 CHANGE_NEXT_HINTS（按**状态**给「下一步」文案）和 changeNextHintOf。
  0824 搬到后端了：每一步自带 hint，随 steps 概览下发，由 ChangeStepBar 显示在当前步上。
  搬的原因是那张表按状态取，而第 1、2 步都发生在 submitted 下——同一个状态下
  两步说同一句话，正是「填两遍」的源头。程序文件里的硬条款（10 个工作日、
  A 级客户书面批准、10.4 反向变更）一条不少地进了后端的 CHANGE_STEPS[].hint。
*/

/*
  这里原来还有一整套「摘格 / 收组」的机件（CHANGE_STEP_FIELDS_NOTE、
  useChangeStepOwnedFields、deferChangeCreateFields、hideStepOwnedFields…）。
  0826 Tony 把变更抽屉里的通用字段区整个删了（「和五步重复」），
  摘谁、收谁的问题随之消失，机件一并拆掉——留着只会让下个人以为它还在生效。
  门禁要读的几格（客户批准、断点标识）搬进了 ChangeStepBar 的第 3、4 步；
  牵头人、关联项目搬进了第 1 步表单；台账费用等其余字段不再有编辑界面，
  但 modules.ts 的字段定义**没删**：导出台账仍按那份出列，存量数据一列不少。
*/

/**
 * 新建工程变更（0826：新建屏 = 第 1 步表单，通用建单表单撤了）。
 *
 * 建单接口两个视图不一样（模块中心 createModuleRecord / 项目页
 * createProjectResource），所以 create 由调用方给；建完的「传附件 → 提交
 * 第 1 步」两步在这里共享——两个视图各写一遍，文案和口径迟早走样。
 *
 * 三段各自报错、各自兜底：单子建成了就**绝不当作整体失败**——
 * 人要知道的是「东西在哪、还差哪一步」，而不是一句笼统的「失败」。
 */
export async function createChangeViaStepForm(
  create: (body: Record<string, unknown>) => Promise<ModuleRecord>,
  payload: {
    values: Record<string, string>;
    file: File | null;
    /** 新建态贴在「客户的变更要求」里的截图（0911 Tony:「Ctrl+V 贴不了附件」） */
    customerImages?: File[];
  },
): Promise<ModuleRecord | null> {
  let record: ModuleRecord;
  try {
    record = await create(buildChangeCreateBody(payload.values));
  } catch (caught) {
    notify('error', '没创建成', errorMessage(caught));
    return null;
  }
  /*
    客户那一格贴进来的截图（0911）：新建态时单子还不存在，图攥在表单手里，
    现在单子有 id 了一并传上去，并打上归属标记——它们是客户给的凭证，
    不该混进「变更内容附件」（0911 第 4 页）。
  */
  const customerImages = payload.customerImages ?? [];
  let customerUploaded = 0;
  for (const image of customerImages) {
    try {
      const bag = new FormData();
      bag.append('file', image, image.name);
      bag.append('note', '客户要求原文');
      await eqsApi.uploadModuleAttachment('changes', 'changes', record.id, bag);
      customerUploaded += 1;
    } catch (caught) {
      notify('error', '客户要求的截图没传上', `${errorMessage(caught)}——在第 1 步重新贴一次。`);
    }
  }
  if (!payload.file) {
    /*
      只贴了客户截图、没选变更内容附件时也算有附件了——第 1 步交得上去。
      所以这里不能一律当草稿打发掉，那样人贴了图还被告知"附件还没传"。
    */
    if (!customerUploaded) {
      notify('success', '已创建（草稿）', '附件还没传：在第 1 步补传相关凭证，再点「提交 / 下一步」。');
      return record;
    }
  } else {
    try {
      const bag = new FormData();
      bag.append('file', payload.file);
      await eqsApi.uploadModuleAttachment('changes', 'changes', record.id, bag);
    } catch (caught) {
      notify('error', '单子已创建，附件没传上', `${errorMessage(caught)}——在第 1 步重新上传，再点「提交 / 下一步」。`);
      return record;
    }
  }
  try {
    const result = await api.post<{ code: string | null }>(
      `module-tools/changes/${record.id}/step-forms/raise`, { ...payload.values },
    );
    notify(
      'success',
      result.code ? `已创建并提交第 1 步，变更编号 ${result.code}` : '已创建并提交第 1 步',
      result.code ? '接着在第 2 步做影响评审。' : '还没挂项目所以暂无编号；在第 1 步挂上项目再提交一次会补。',
    );
  } catch (caught) {
    notify('error', '单子和附件都在了，第 1 步没提交上', `${errorMessage(caught)}——按提示补完，再点「提交 / 下一步」。`);
  }
  return record;
}

/** 建单 payload：和通用 save 同一套拆分口径（核心列进顶层，其余进 normalized） */
export function buildChangeCreateBody(values: Record<string, string>): Record<string, unknown> {
  const coreKeys = new Set(['title', 'ownerName', 'projectId']);
  const body: Record<string, unknown> = {};
  const normalized: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(values)) {
    const value = (raw ?? '').trim();
    if (!value) continue;
    if (coreKeys.has(key)) body[key] = value;
    else normalized[key] = value;
  }
  if (body.projectId !== undefined) body.projectId = Number(body.projectId);
  if (Object.keys(normalized).length) body.normalized = normalized;
  return body;
}

/**
 * 变更抽屉的抬头：`编辑工程变更 · ECN-X04C-001 出风口叶片加强筋`。
 *
 * **只有变更这么做**：变更把编号和标题摘进了第 1 步表单，抬头再写死
 * 「编辑工程变更」就等于打开一条单子看不出自己在看哪条，而变更编号是
 * 程序文件规定的唯一追溯码。别的模块的编号和标题还在表单第一格摆着，
 * 不为了「一致」去动它们的界面。
 *
 * 缺哪个补哪个：只有编号就只显示编号，两个都没有就退回原文案——
 * 「编辑工程变更 · 」这种拖着分隔符的空尾巴比不显示更难看。
 */
export function changeDialogTitle(
  base: string,
  record: ModuleRecord | null | undefined,
  titleLimit = 24,
): string {
  if (!record) return base;
  const code = String(record.code ?? '').trim();
  const rawTitle = String(record.title ?? '').trim();
  // 抬头是一行，长标题截断加省略号，别把关闭按钮挤出去
  const title = rawTitle.length > titleLimit ? `${rawTitle.slice(0, titleLimit)}…` : rawTitle;
  const identity = [code, title].filter(Boolean).join(' ');
  return identity ? `${base} · ${identity}` : base;
}

export function useChangeChecklist(
  selected: Ref<ModuleRecord | null>,
  onChanged?: () => void | Promise<void>,
) {
  const generating = ref(false);
  const reminding = ref(false);

  async function generate(): Promise<void> {
    if (!selected.value || generating.value) return;
    generating.value = true;
    try {
      const result = await eqsApi.startChangeChecklist(selected.value.id as number);
      /*
        0826 起生成是**按 code 补铺**：老单子（老 10 条 / 37+4 时代生成的）再点一次，
        会把 44 条新模板里缺的补上、已有的一条不动。三种结果照实说。
      */
      if (!result.created) {
        notify('success', '清单已经是全的', '标准动作一条不缺，没有要补的。');
      } else if (result.alreadyStarted) {
        notify('success', `已补齐 ${result.created} 条标准动作`,
          '原有的动作一条没动；新补的按阶段归组，逐条指派责任人。');
      } else {
        notify('success', '执行清单已生成', `已铺好 ${result.created} 条动作，逐条指派责任人即可。`);
      }
      await onChanged?.();
    } catch (caught) {
      notify('error', '生成清单失败', errorMessage(caught));
    } finally {
      generating.value = false;
    }
  }

  /** 催不到谁必须报出来——不能假装都催到了 */
  async function remind(): Promise<void> {
    if (!selected.value || reminding.value) return;
    reminding.value = true;
    try {
      const result = await eqsApi.remindChangeActions(selected.value.id as number);
      const misses: string[] = [];
      if (result.noOwner.length) misses.push(`${result.noOwner.length} 条没写责任人`);
      if (result.unresolved.length) misses.push(`${result.unresolved.length} 条责任人对不上账号`);
      if (!result.pending) {
        notify('success', '没有要催的', '措施都已完成或设了不适用。');
      } else if (misses.length) {
        notify('error', `催到 ${result.notified}/${result.pending} 条`,
          `${misses.join('；')}——先把责任人补上，否则永远催不到。`);
      } else {
        notify('success', `已催办 ${result.notified} 条`, '责任人会收到站内提醒；同一条一天只催一次。');
      }
    } catch (caught) {
      notify('error', '催办失败', errorMessage(caught));
    } finally {
      reminding.value = false;
    }
  }

  return { generating, reminding, generate, remind };
}
